// @ts-nocheck
import './styles.scss';
import { Table, TableContainer, TableBody, TableHead, TableRow } from '@mui/material';

import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import Switch from '../../../common/switch';
import l from '../../../../lang';
import { styled } from '@mui/styles';
import { getCountryFlagUrl } from '../../../../utils';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { canModifyPaymentMethods } from '../../../../store/payment-methods/roles';
import './payer-select.scss';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { ReactComponent as InfoIcon } from '../../../../assets/icons/info-unfilled.svg';
import { Select as MuiSelect, MenuItem } from '@mui/material';
import TooltipIcon from '../../../common/tooltip-icon';
import { useInstallmentsInfo } from '../../../../hooks/use-installments-info';
import Loading from '../../../common/loading';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#fafafa',
  },
  [`&.${tableCellClasses.head}:first-child `]: {
    borderTopLeftRadius: '8px',
  },
  [`&.${tableCellClasses.head}:last-child `]: {
    borderTopRightRadius: '8px',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const PayerSelect = ({ value, onChange, disabled }) => (
  <MuiSelect
    value={value}
    onChange={e => onChange(e.target.value)}
    disabled={disabled}
    className="payer-select"
    IconComponent={ArrowDropDownIcon}
  >
    <MenuItem value={'END_USER'}>{l('paymentMethod.installments.buyer')}</MenuItem>
    <MenuItem value={'MERCHANT'}>{l('paymentMethod.installments.merchant')}</MenuItem>
  </MuiSelect>
);

const countryTooltipMap: Record<string, string> = {
  AR: 'paymentMethod.installments.tooltip.argentina',
  UY: 'paymentMethod.installments.tooltip.uruguay',
};

function Installments() {
  const { installmentsCountryInfos, disableInstallmentsCountry, enableInstallmentsCountry, loading } = useInstallmentsInfo();
  const { auth } = useSelector(state => state);
  const canModify = canModifyPaymentMethods(auth?.roles);
  const [payerByCountry, setPayerByCountry] = useState(() => {
    const initial = {} as Record<string, string | null>;
    installmentsCountryInfos.forEach(row => {
      initial[row.countryCode] = row.paymentResponsible ? row.paymentResponsible : null;
    });
    return initial;
  });

  const handlePayerChange = (countryCode: string, value: string) => {
    setPayerByCountry(prev => ({ ...prev, [countryCode]: value }));
    const row = installmentsCountryInfos.find(r => r.countryCode === countryCode);
    if (row && row.enabled) {
      enableInstallmentsCountry(countryCode, value);
    } else if (row && !row.enabled) {
      disableInstallmentsCountry(countryCode, value);
    }
  };

  return (
    <div>
      <div className="payment-methods__subtitle">{l('paymentMethod.installments.subtitle')}</div>
      <div className="payment-methods__desc">{l('paymentMethod.installments.desc')}</div>
      {loading && (
        <div className="payment-methods__loading">
          <Loading />
        </div>
      )}
      {!loading && (
        <TableContainer>
          <Table sx={{ minWidth: 700 }} aria-label="installments table">
            <TableHead>
              <TableRow>
                <StyledTableCell>{l('paymentMethod.installments.header.country')}</StyledTableCell>
                <StyledTableCell size="small">{l('paymentMethod.installments.header.available')}</StyledTableCell>
                <StyledTableCell size="small">Enabled</StyledTableCell>
                <StyledTableCell>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {l('paymentMethod.installments.header.paymentResponsible')}
                    <TooltipIcon
                      placement="bottom"
                      text={l('paymentMethod.installments.tooltip.paymentResponsible')}
                      icon={<InfoIcon />}
                    />
                  </span>
                </StyledTableCell>
                <StyledTableCell>{l('paymentMethod.installments.header.cost')}</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {installmentsCountryInfos.map(row => (
                <TableRow key={row.countryCode}>
                  <TableCell component="th" scope="row">
                    <div className="payment-method-list__country">
                      <img
                        alt={`img-country-${row.countryCode}`}
                        className="payment-method-list__country-flag"
                        src={getCountryFlagUrl(row.countryCode)}
                      />
                      <span>{l(`countryCode.${row.countryCode}`)}</span>
                      {row.hasNote && <span>*</span>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="installments-toggle-row">
                      <span className="installments-toggle-label">
                        {row.available ? l('common.yes') : l('common.no')}
                        <span className="installments-toggle-tooltip">
                          {countryTooltipMap[row.countryCode as string] ? (
                            <TooltipIcon
                              placement="bottom"
                              text={l(countryTooltipMap[row.countryCode as string])}
                              icon={<InfoIcon style={{ width: 18, height: 18 }} />}
                            />
                          ) : null}
                        </span>
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="installments-toggle-switch">
                      <Switch
                        checked={row.available && row.enabled}
                        onChange={() => {
                          const payer =
                            payerByCountry[row.countryCode] ||
                            (row.paymentResponsible === 'merchant' ? 'MERCHANT' : 'END_USER');
                          if (row.enabled) {
                            disableInstallmentsCountry(row.countryCode, payer);
                          } else {
                            enableInstallmentsCountry(row.countryCode, payer);
                          }
                        }}
                        disabled={!row.available || !row.isInstallmentsAllowed || !canModify}
                      />
                    </div>
                  </TableCell>
                  <TableCell>
                    {!row.available ? (
                      l('paymentMethod.installments.noApply')
                    ) : (
                      <PayerSelect
                        value={
                          payerByCountry[row.countryCode] ||
                          (row.paymentResponsible === 'merchant' ? 'MERCHANT' : 'END_USER')
                        }
                        onChange={val => handlePayerChange(row.countryCode, val)}
                        disabled={!row.available || !row.enabled || !row.isInstallmentsAllowed || !row.payerSelectable || !canModify}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {!row.available ? (
                      l('paymentMethod.installments.noApply')
                    ) : row.cost == 'issuer' ? (
                      l('paymentMethod.installments.issuerCost')
                    ) : (
                      <a
                        href="https://40013370.fs1.hubspotusercontent-na1.net/hubfs/40013370/Pricing/dLocal%20Go_Installments.pdf"
                        target="_blank"
                      >
                        {l('paymentMethod.installments.merchantCost')}
                      </a>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <span className="installments-note">{l('paymentMethod.installments.argentinaNote')}</span>
    </div>
  );
}

export default Installments;
